import React, { useState } from 'react';
import type { PhotoFocus, PublicAgent } from '../sharePublicApi';

const initials = (name: string | null) =>
  (name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join('');

/** Face width as a share of the circle — leaves room for hair and a little shoulder. */
const FACE_SHARE = 0.46;
/** Where the face centre lands, as a share of the circle (a touch low: hair sits above the box). */
const FACE_CENTRE_Y = 0.56;

const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));

/**
 * Size and offset of the photo, in % of the circle, so the detected face fills
 * the avatar. The photo always covers the whole circle.
 */
export function focusedPhotoStyle(focus: PhotoFocus): React.CSSProperties {
  const width = Math.max(FACE_SHARE / focus.w, 1, 1 / focus.aspect);
  const height = width * focus.aspect;
  const left = clamp(0.5 - (focus.x + focus.w / 2) * width, 1 - width, 0);
  const top = clamp(FACE_CENTRE_Y - (focus.y + focus.h / 2) * height, 1 - height, 0);
  return {
    position: 'absolute',
    maxWidth: 'none',
    width: `${width * 100}%`,
    height: `${height * 100}%`,
    left: `${left * 100}%`,
    top: `${top * 100}%`,
  };
}

export function AgentAvatar({ agent, size }: { agent: PublicAgent; size: number }) {
  const [failed, setFailed] = useState(false);
  const style = { width: size, height: size, fontSize: size * 0.38 };
  if (agent.photo && !failed) {
    if (agent.photoFocus) {
      return (
        <span className="sp-avatar sp-avatar--framed" style={style}>
          <img src={agent.photo} alt="" style={focusedPhotoStyle(agent.photoFocus)} onError={() => setFailed(true)} />
        </span>
      );
    }
    return <img className="sp-avatar" src={agent.photo} alt="" style={style} onError={() => setFailed(true)} />;
  }
  return <span className="sp-avatar" style={style} aria-hidden="true">{initials(agent.name)}</span>;
}
